/** Practice track sections for problem placement. */
export const PROBLEM_SECTIONS = [
  'Getting Started',
  'Patterns',
  'Number System',
  'Arrays',
  'Searching & Sorting',
  '2-D Array',
  'Strings',
  'Recursion & Backtracking',
  'Time & Space Complexity',
  'Stack and Queues',
  'Linked List',
  'Generic Trees',
  'Binary Trees',
  'Binary Search Trees',
  'HashMap & Heap',
  'Graph',
  'Dynamic Programming',
  'Trie',
  'Contest',
];

export const TOPIC_SUGGESTIONS = [
  'array',
  'hash-table',
  'two-pointers',
  'sliding-window',
  'binary-search',
  'dp',
  'graph',
  'tree',
  'linked-list',
  'stack',
  'queue',
  'greedy',
  'backtracking',
  'math',
  'string',
];

export const PROBLEM_FORM_TABS = [
  { id: 'basics', label: 'Basics', hint: 'Title, difficulty, topics' },
  { id: 'statement', label: 'Statement', hint: 'Description & constraints' },
  { id: 'tests', label: 'Tests & code', hint: 'Samples, hidden tests, stubs' },
  { id: 'placement', label: 'Placement', hint: 'Track section & order' },
];

export const FUNCTION_MODE_PRESETS = [
  {
    id: 'array-return',
    label: 'Array → array',
    signature: {
      className: 'Solution',
      methodName: 'solve',
      params: [{ name: 'nums', type: 'int[]' }],
      returnType: 'int[]',
    },
    sampleCase: { args: { nums: [1, 2, 3] }, expected: [1, 2, 3], isSample: true, explanation: '' },
  },
  {
    id: 'two-sum',
    label: 'Two Sum style',
    signature: {
      className: 'Solution',
      methodName: 'twoSum',
      params: [
        { name: 'nums', type: 'int[]' },
        { name: 'target', type: 'int' },
      ],
      returnType: 'int[]',
    },
    sampleCase: {
      args: { nums: [2, 7, 11, 15], target: 9 },
      expected: [0, 1],
      isSample: true,
      explanation: 'nums[0] + nums[1] == 9',
    },
  },
  {
    id: 'reverse-in-place',
    label: 'Reverse array (void)',
    signature: {
      className: 'Solution',
      methodName: 'reverse',
      params: [{ name: 'nums', type: 'int[]' }],
      returnType: 'void',
      outputMode: 'args',
      assertOn: ['nums'],
    },
    sampleCase: {
      args: { nums: [1, 2, 3, 4, 5] },
      expected: { nums: [5, 4, 3, 2, 1] },
      isSample: true,
      explanation: 'Reverse in place',
    },
  },
  {
    id: 'linked-list',
    label: 'Linked list',
    signature: {
      className: 'Solution',
      methodName: 'reverseList',
      params: [{ name: 'head', type: 'ListNode' }],
      returnType: 'ListNode',
    },
    sampleCase: {
      args: { head: [1, 2, 3, 4, 5] },
      expected: [5, 4, 3, 2, 1],
      isSample: true,
      explanation: '1→2→3→4→5 becomes 5→4→3→2→1',
    },
  },
  {
    id: 'binary-tree',
    label: 'Binary tree',
    signature: {
      className: 'Solution',
      methodName: 'invertTree',
      params: [{ name: 'root', type: 'TreeNode' }],
      returnType: 'TreeNode',
    },
    sampleCase: {
      args: { root: [4, 2, 7, 1, 3, 6, 9] },
      expected: [4, 7, 2, 9, 6, 3, 1],
      isSample: true,
      explanation: 'Level-order: invert left/right children',
    },
  },
];

/** Insert-able helper snippets for admin (comment-only — real classes live in harness). */
export const STUB_HELPER_TEMPLATES = {
  ListNode: {
    python: `# Definition for singly-linked list.
# class ListNode:
#     def __init__(self, val, next=None):
#         self.val = val
#         self.next = next`,
    javascript: `// Definition for singly-linked list.
// class ListNode {
//   constructor(val, next = null) {
//     this.val = val;
//     this.next = next;
//   }
// }`,
    java: `/**
 * Definition for singly-linked list.
 * class ListNode {
 *     int val;
 *     ListNode next;
 *     ListNode(int val, ListNode next) { this.val = val; this.next = next; }
 * }
 */`,
    cpp: `// Definition for singly-linked list.
// struct ListNode {
//     int val;
//     ListNode *next;
//     ListNode(int val, ListNode *next) : val(val), next(next) {}
// };`,
    csharp: `// Definition for singly-linked list.
// public class ListNode {
//     public int val;
//     public ListNode next;
//     public ListNode(int val, ListNode next) { this.val = val; this.next = next; }
// }`,
  },
  TreeNode: {
    python: `# Definition for a binary tree node.
# class TreeNode:
#     def __init__(self, val, left=None, right=None):
#         self.val = val
#         self.left = left
#         self.right = right`,
    javascript: `// Definition for a binary tree node.
// class TreeNode {
//   constructor(val, left = null, right = null) {
//     this.val = val;
//     this.left = left;
//     this.right = right;
//   }
// }`,
    java: `/**
 * Definition for a binary tree node.
 * class TreeNode {
 *     int val;
 *     TreeNode left, right;
 *     TreeNode(int val, TreeNode left, TreeNode right) {
 *         this.val = val; this.left = left; this.right = right;
 *     }
 * }
 */`,
    cpp: `// Definition for a binary tree node.
// struct TreeNode {
//     int val;
//     TreeNode *left, *right;
//     TreeNode(int val, TreeNode *left, TreeNode *right)
//         : val(val), left(left), right(right) {}
// };`,
    csharp: `// Definition for a binary tree node.
// public class TreeNode {
//     public int val;
//     public TreeNode left, right;
//     public TreeNode(int val, TreeNode left, TreeNode right) {
//         this.val = val; this.left = left; this.right = right;
//     }
// }`,
  },
  Node: {
    python: `# Definition for an n-ary tree node.
# class Node:
#     def __init__(self, val, children=None):
#         self.val = val
#         self.children = children if children is not None else []`,
    javascript: `// Definition for an n-ary tree node.
// class Node {
//   constructor(val, children = []) {
//     this.val = val;
//     this.children = children;
//   }
// }`,
    java: `/**
 * Definition for an n-ary tree node.
 * class Node {
 *     int val;
 *     List<Node> children;
 *     Node(int val, List<Node> children) {
 *         this.val = val; this.children = children;
 *     }
 * }
 */`,
    cpp: `// Definition for an n-ary tree node.
// class Node {
// public:
//     int val;
//     vector<Node*> children;
//     Node(int val, vector<Node*> children) : val(val), children(std::move(children)) {}
// };`,
    csharp: `// Definition for an n-ary tree node.
// public class Node {
//     public int val;
//     public IList<Node> children;
//     public Node(int val, IList<Node> children) {
//         this.val = val; this.children = children;
//     }
// }`,
  },
};
